import ObjectUtil from "../../util/ObjectUtil"
import { WorkspaceEvaluationContext, WorkspaceEvaluationContextKey } from "./WorkspaceEvaluationContext"

export interface WorkspaceEvaluationCache {
  get(key: WorkspaceEvaluationContextKey): WorkspaceEvaluationContext | undefined

  put(context: WorkspaceEvaluationContext): WorkspaceEvaluationContext[]

  latest(): WorkspaceEvaluationContext | undefined

  restore(contexts: WorkspaceEvaluationContext[]): void
}

/**
 * Bounded per-user cache keyed by the context key. Uses a `Map`, whose insertion order gives the
 * recency order: `put` moves the key to the most-recent position and evicts the oldest once the
 * capacity is exceeded.
 */
export class LruWorkspaceEvaluationCache implements WorkspaceEvaluationCache {
  private readonly entries = new Map<string, WorkspaceEvaluationContext>()

  constructor(private readonly capacity: number) {}

  get(key: WorkspaceEvaluationContextKey): WorkspaceEvaluationContext | undefined {
    return this.entries.get(key.hashKey())
  }

  put(context: WorkspaceEvaluationContext): WorkspaceEvaluationContext[] {
    const isStale = this.isStaleContext(context)
    if (isStale) {
      return Array.from(this.entries.values())
    }

    const hashKey = context.key.hashKey()

    this.entries.delete(hashKey)
    this.entries.set(hashKey, context)
    this.evict()
    return Array.from(this.entries.values())
  }

  latest(): WorkspaceEvaluationContext | undefined {
    const contexts = Array.from(this.entries.values())
    return contexts[contexts.length - 1]
  }

  restore(contexts: WorkspaceEvaluationContext[]): void {
    this.entries.clear()
    // Keep the most-recent `capacity` contexts (the tail). Not `slice(-capacity)`: `-0` would
    // keep everything, so a capacity of 0 must resolve to an empty cache.
    for (const context of contexts.slice(Math.max(0, contexts.length - this.capacity))) {
      this.entries.set(context.key.hashKey(), context)
    }
  }

  private evict(): void {
    if (this.entries.size > this.capacity) {
      const oldest = this.entries.keys().next().value
      this.entries.delete(oldest)
    }
  }

  private isStaleContext(context: WorkspaceEvaluationContext): boolean {
    const exist = this.get(context.key)
    return (
      ObjectUtil.isNotNullOrUndefined(exist) &&
      exist.workspace.metadata.evaluatedAt > context.workspace.metadata.evaluatedAt
    )
  }
}
